import noUiSlider from "nouislider";
import CalcUtilities from "./CalcUtilities";
import {
  DEFAULT_LOAN_AMOUNT,
  LOAN_AMOUNT_STEP,
  DISPLAY_VALUE_TEXT_SELECTOR,
  MIN_MAX_TEXT_SELECTOR,
} from "./constants";

/**
 * CalcSlider класс для управления слайдером кредитного калькулятора
 */
export default class CalcSlider {
  /**
   * Создает экземпляр CalcSlider
   * @param {HTMLElement} sliderElement - DOM элемент слайдера
   * @param {Function} onSlideCallback - Функция обратного вызова при изменении слайдера
   */
  constructor(sliderElement, onSlideCallback) {
    this.sliderElement = sliderElement;
    this.onSlideCallback = onSlideCallback;
    this.displayElement = document.querySelector(DISPLAY_VALUE_TEXT_SELECTOR);
    this.minMaxElement = document.querySelector(MIN_MAX_TEXT_SELECTOR);
  }

  /**
   * Инициализирует слайдер с начальными параметрами
   * @param {number} minAmount - Минимальная сумма
   * @param {number} maxAmount - Максимальная сумма
   * @param {number} startValue - Начальное значение
   */
  initialize(minAmount, maxAmount, startValue = DEFAULT_LOAN_AMOUNT) {
    if (!this.sliderElement) {
      return;
    }

    noUiSlider.create(this.sliderElement, {
      range: {
        min: minAmount,
        max: maxAmount,
      },
      start: startValue,
      step: LOAN_AMOUNT_STEP,
      connect: "lower",
    });

    this._attachSlideListener();
    this._updateDisplayValue(startValue);
    this._updateMinMaxDisplay(minAmount, maxAmount);
  }

  /**
   * Обновляет диапазон слайдера
   * @param {number} minAmount - Новое минимальное значение
   * @param {number} maxAmount - Новое максимальное значение
   */
  updateRange(minAmount, maxAmount) {
    if (!this.sliderElement || !this.sliderElement.noUiSlider) {
      return;
    }

    const currentValue = this.getValue();

    // Определяем новое значение в пределах нового диапазона
    let newValue = currentValue;
    if (currentValue < minAmount) {
      newValue = minAmount;
    } else if (currentValue > maxAmount) {
      newValue = maxAmount;
    }

    // Уничтожаем старый слайдер и создаем новый
    this.sliderElement.noUiSlider.destroy();

    noUiSlider.create(this.sliderElement, {
      range: {
        min: minAmount,
        max: maxAmount,
      },
      start: newValue,
      step: LOAN_AMOUNT_STEP,
      connect: "lower",
    });

    this._attachSlideListener();
    this._updateDisplayValue(newValue);
    this._updateMinMaxDisplay(minAmount, maxAmount);

    // Вызываем callback для пересчета
    if (this.onSlideCallback) {
      this.onSlideCallback();
    }
  }

  /**
   * Получает текущее значение слайдера
   * @returns {number} Текущее значение
   */
  getValue() {
    if (!this.sliderElement || !this.sliderElement.noUiSlider) {
      return DEFAULT_LOAN_AMOUNT;
    }
    return parseInt(this.sliderElement.noUiSlider.get(), 10);
  }

  /**
   * Устанавливает значение слайдера
   * @param {number} value - Новое значение
   */
  setValue(value) {
    if (!this.sliderElement || !this.sliderElement.noUiSlider) {
      return;
    }
    this.sliderElement.noUiSlider.set(value);
  }

  /**
   * Обновляет отображение текущего значения
   * @private
   * @param {number} value - Значение для отображения
   */
  _updateDisplayValue(value) {
    if (this.displayElement) {
      this.displayElement.innerHTML = CalcUtilities.formatNumber(value);
    }
  }

  /**
   * Обновляет отображение минимальной и максимальной сумм
   * @private
   * @param {number} minAmount - Минимальная сумма
   * @param {number} maxAmount - Максимальная сумма
   */
  _updateMinMaxDisplay(minAmount, maxAmount) {
    if (!this.minMaxElement) {
      return;
    }

    const minText = this.minMaxElement.querySelector(
      ".min-max-block__min-text",
    );
    const maxText = this.minMaxElement.querySelector(
      ".min-max-block__max-text",
    );

    if (minText) {
      minText.textContent = `${CalcUtilities.formatNumber(minAmount)} ₸`;
      minText.dataset.minValue = minAmount;
    }

    if (maxText) {
      maxText.textContent = `${CalcUtilities.formatNumber(maxAmount)} ₸`;
      maxText.dataset.maxValue = maxAmount;
    }
  }

  /**
   * Прикрепляет слушатель к событию изменения слайдера
   * @private
   */
  _attachSlideListener() {
    if (!this.sliderElement || !this.sliderElement.noUiSlider) {
      return;
    }

    CalcUtilities.bindSliderAria(this.sliderElement, "Сумма микрокредита");

    this.sliderElement.noUiSlider.on("slide", () => {
      const newValue = this.getValue();
      this._updateDisplayValue(newValue);

      // Вызываем callback для пересчета
      if (this.onSlideCallback) {
        this.onSlideCallback();
      }
    });
  }
}
